import { utils, write } from '@e965/xlsx';

export interface IEExportRow {
  lin: string;
  nsn: string;
  nomenclature: string;
  quantity: number;
  optional: boolean;
  notes: string;
}

/**
 * Builds an .xlsx Blob from IE kit items and triggers a file download.
 * The exported template can be edited and re-imported via the IE Kit import flow.
 */
export const exportIeKitToXlsx = (rows: IEExportRow[], kitName: string, ieKitLabel = 'IE Kit'): void => {
  const headers = ['LIN', 'NSN', 'Nomenclature', 'Quantity', 'Optional', 'Notes'];

  const dataRows = rows.map((row) => [
    row.lin,
    row.nsn,
    row.nomenclature,
    row.quantity,
    row.optional ? 'Yes' : 'No',
    row.notes,
  ]);

  const ws = utils.aoa_to_sheet([headers, ...dataRows]);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, ieKitLabel);

  const buffer = write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const safeName = kitName.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80) || 'ie_kit';
  const filename = `${safeName}_template.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60000);
};

/**
 * Downloads a blank IE Kit import template for users to fill in.
 */
export const downloadBlankIeTemplate = (ieKitLabel = 'IE Kit'): void => {
  exportIeKitToXlsx([], 'ie_import', ieKitLabel);
};

/**
 * Exports validation errors as an .xlsx file with an Error column.
 */
export const exportIeErrorReport = (
  errors: Array<{ rowNumber: number; lin: string; nsn: string; nomenclature: string; quantity: string; optional: string; notes: string; error: string }>,
  kitName: string,
): void => {
  const headers = ['Row', 'LIN', 'NSN', 'Nomenclature', 'Quantity', 'Optional', 'Notes', 'Error'];

  const dataRows = errors.map((row) => [
    row.rowNumber,
    row.lin,
    row.nsn,
    row.nomenclature,
    row.quantity,
    row.optional,
    row.notes,
    row.error,
  ]);

  const ws = utils.aoa_to_sheet([headers, ...dataRows]);
  const wb = utils.book_new();
  utils.book_append_sheet(wb, ws, 'Errors');

  const buffer = write(wb, { bookType: 'xlsx', type: 'array' }) as ArrayBuffer;
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });

  const safeName = kitName.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 80) || 'ie_errors';
  const filename = `${safeName}_errors.xlsx`;
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60000);
};
